import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Check,
  ChevronRight,
  IndianRupee,
  FileText,
  Upload,
  X,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useAlert } from "components/UI_components/Alert";
import { ContractorApi } from "api/contractors/service";
import { resolveTransactionFeedback } from "utils/transactionFeedback";
import { getLinkedTasksBySite } from "utils/linkedTasks";
import {
  buildContractorPaymentTaskUpdate,
  createLinkedTaskUpdate,
} from "utils/taskAutoUpdates";
import { Search, Link as LinkIcon } from "lucide-react";

// Utilities
const cn = (...classes) => classes.filter(Boolean).join(" ");

const Card = ({ className = "", children }) => (
  <div
    className={`rounded-lg border bg-white text-card-foreground shadow-sm ${className}`}
  >
    {children}
  </div>
);

const CardContent = ({ className = "", children }) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

const Button = ({
  variant = "default",
  size = "default",
  className = "",
  children,
  ...props
}) => {
  const baseStyles =
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
  const variants = {
    default: "bg-sm-primary text-white shadow hover:bg-sm-primary/90",
    destructive:
      "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
    outline:
      "border border-input bg-transparent shadow-sm hover:bg-sm-accent hover:text-sm-accent-foreground",
    secondary:
      "bg-sm-secondary text-sm-secondary-foreground shadow-sm hover:bg-sm-secondary/80",
    ghost: "hover:bg-sm-accent hover:text-sm-accent-foreground",
    link: "text-sm-primary underline-offset-4 hover:underline",
  };
  const sizes = {
    default: "h-9 px-4 py-2",
    sm: "h-8 rounded-md px-3 text-xs",
    lg: "h-10 rounded-md px-8",
    icon: "h-9 w-9",
  };
  return (
    <button
      className={cn(baseStyles, variants[variant], sizes[size], className)}
      {...props}
    >
      {children}
    </button>
  );
};

const Input = ({ className = "", ...props }) => (
  <input
    className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Label = ({ children, className = "" }) => (
  <label
    className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${className}`}
  >
    {children}
  </label>
);

const Textarea = ({ className = "", ...props }) => (
  <textarea
    className={`flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Badge = ({ variant = "default", className = "", children }) => {
  const baseStyles =
    "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
  const variants = {
    default:
      "border-transparent bg-sm-primary text-sm-primary-foreground hover:bg-sm-primary/80",
    secondary:
      "border-transparent bg-sm-secondary/10 text-sm-secondary hover:bg-sm-secondary/20",
    outline: "text-foreground",
  };
  const finalClass = className.includes("bg-")
    ? `${baseStyles} ${className}`
    : cn(baseStyles, variants[variant], className);
  return <div className={finalClass}>{children}</div>;
};

const sanitizeAmountInput = (value) => {
  const stringValue = String(value ?? "");
  if (!stringValue) return "";

  const sanitized = stringValue.replace(/[^\d.]/g, "");
  const [integerPart = "", ...decimalParts] = sanitized.split(".");
  const mergedDecimals = decimalParts.join("").slice(0, 2);

  if (sanitized.startsWith(".")) {
    return mergedDecimals ? `0.${mergedDecimals}` : "0.";
  }

  if (decimalParts.length === 0) return integerPart;
  return `${integerPart}.${mergedDecimals}`;
};

const formatAmountToTwoDecimals = (value) => {
  const parsedAmount = Number.parseFloat(value);
  if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) return "";
  return parsedAmount.toFixed(2);
};

const hasExactlyTwoDecimals = (value) =>
  /^\d+(\.\d{2})$/.test(String(value ?? "").trim());

const isAmountEntryParsable = (value) =>
  /^\d+(\.\d{0,2})?$/.test(String(value ?? "").trim());

export function AddPaymentDialog({
  open,
  onOpenChange,
  workOrders,
  onAddPayment,
}) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [workOrderDetails, setWorkOrderDetails] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState({
    amount: "",
    mode: "",
    date: new Date().toISOString().split("T")[0],
    reference: "",
    remarks: "",
  });
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState(null);
  const { addAlert } = useAlert();

  // BoQ linking states
  const [boqItems, setBoqItems] = useState([]);
  const [showBoQModal, setShowBoQModal] = useState(false);
  const [selectedBoQ, setSelectedBoQ] = useState(null);
  const [boqSearchQuery, setBoqSearchQuery] = useState("");

  // Fetch linked tasks for the selected work order's site
  useEffect(() => {
    const fetchLinkedTasks = async () => {
      if (!workOrderDetails?.site_id) {
        setBoqItems([]);
        return;
      }

      try {
        const linkedTasks = await getLinkedTasksBySite(
          workOrderDetails.site_id,
        );
        setBoqItems(linkedTasks);
      } catch (error) {
        console.error("Error fetching linked tasks:", error);
      }
    };
    fetchLinkedTasks();
  }, [workOrderDetails]);

  useEffect(() => {
    return () => {
      if (attachmentPreviewUrl) {
        URL.revokeObjectURL(attachmentPreviewUrl);
      }
    };
  }, [attachmentPreviewUrl]);

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "₹0";
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount.toLocaleString()}`;
  };

  const updatePaymentAmount = (amount) => {
    setPaymentDetails((prev) => ({ ...prev, amount }));
  };

  const handleSelectWorkOrder = async (woId) => {
    // console.log("wo id", woId);
    const wo = workOrders.find((w) => w.workorder_id === parseInt(woId));
    if (wo) {
      setSelectedWorkOrder(wo);
      // console.log(wo);
      setLoading(true);
      setError(null);
      try {
        const response = await ContractorApi.getWorkOrderDetail(woId);
        if (response.status) {
          setWorkOrderDetails(response.data);
        } else {
          setError("Failed to fetch work order details");
        }
      } catch (err) {
        setError("An error occurred while fetching details");
      } finally {
        setLoading(false);
      }
      updatePaymentAmount("");
      setSelectedMilestone(null);
      setSelectedMonth(null);
    }
  };

  const handleSelectMilestone = (milestone) => {
    setSelectedMilestone(milestone);
    const maxPayable = milestone.amount - milestone.paid_so_far;
    updatePaymentAmount(formatAmountToTwoDecimals(maxPayable));
  };

  const handleSelectMonth = (bill, index) => {
    setSelectedMonth({ ...bill, index: index + 1 });
    // For monthly running bill, amount is read-only and equal to monthly_bill.amount
    updatePaymentAmount(
      formatAmountToTwoDecimals(bill.amount - bill.paid_so_far),
    );
  };

  const handleAmountChange = (event) => {
    setError(null);
    updatePaymentAmount(sanitizeAmountInput(event.target.value));
  };

  const handleNext = () => {
    if (step === 1 && selectedWorkOrder && workOrderDetails) {
      setStep(2);
    } else if (step === 2 && paymentDetails.amount && paymentDetails.mode) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleAttachmentChange = (event) => {
    const file = event.target.files?.[0] || null;
    setAttachmentFile(file);
    setAttachmentPreviewUrl(
      file?.type?.startsWith("image/") ? URL.createObjectURL(file) : null,
    );
  };

  const clearAttachment = () => {
    setAttachmentFile(null);
    setAttachmentPreviewUrl(null);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleConfirm = async () => {
    if (workOrderDetails && paymentDetails.amount && paymentDetails.mode) {
      const formattedAmount = formatAmountToTwoDecimals(paymentDetails.amount);
      if (!formattedAmount || !hasExactlyTwoDecimals(formattedAmount)) {
        setError("Amount must be entered with exactly two decimal places.");
        return;
      }
      if (!isAmountValid(formattedAmount)) {
        setError("Amount exceeds balance due");
        return;
      }

      setSubmitting(true);
      setError(null);

      const formData = new FormData();
      formData.append("work_order", workOrderDetails.id);
      formData.append("amount", formattedAmount);
      formData.append("payment_mode", paymentDetails.mode);
      formData.append("payment_date", paymentDetails.date);
      formData.append("reference_no", paymentDetails.reference);
      const defaultRemarks =
        workOrderDetails.payment_structure === "MILESTONE_BASED"
          ? `Milestone payment: ${selectedMilestone?.name}`
          : workOrderDetails.payment_structure === "MONTHLY_RUNNING_BILL"
            ? `Monthly running bill payment: ${selectedMonth?.month}`
            : "Fixed Price payment(Custom)";
      formData.append("remarks", paymentDetails.remarks || defaultRemarks);
      if (
        workOrderDetails.payment_structure === "MILESTONE_BASED" &&
        selectedMilestone
      ) {
        formData.append("milestone", selectedMilestone.id);
      }
      if (
        workOrderDetails.payment_structure === "MONTHLY_RUNNING_BILL" &&
        selectedMonth
      ) {
        formData.append("month_index", selectedMonth.index);
      }
      if (attachmentFile) {
        formData.append("attachment_file", attachmentFile);
      }
      if (selectedBoQ?.task) {
        formData.append("task_id", selectedBoQ.task);
      }
      // milestone/month_index already appended to formData above

      try {
        const response = await ContractorApi.createPayment(formData);
        const feedback = resolveTransactionFeedback(response, {
          successMessage: "Payment recorded successfully",
          errorMessage: "Failed to record payment",
        });

        if (feedback.success) {
          const contractorId =
            response?.data?.contractor_id || selectedWorkOrder?.contractorId;
          const paymentLabel =
            selectedMilestone?.name ||
            selectedMonth?.month ||
            workOrderDetails.payment_structure?.replace(/_/g, " ");

          if (feedback.approvalRequired) {
            addAlert(feedback.message, feedback.alertType);
          }

          await createLinkedTaskUpdate(
            selectedBoQ?.task,
            buildContractorPaymentTaskUpdate({
              record: response,
              amount: paymentDetails.amount,
              contractorName: workOrderDetails.contractor_name,
              paymentLabel,
            }),
          );

          onAddPayment && contractorId && onAddPayment(contractorId);
          handleClose();
        } else {
          setError(feedback.message);
        }
      } catch (err) {
        setError(
          resolveTransactionFeedback(err?.data, {
            errorMessage: err?.message || "An error occurred during submission",
          }).message,
        );
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedWorkOrder(null);
    setWorkOrderDetails(null);
    setSelectedMilestone(null);
    setSelectedMonth(null);
    setError(null);
    setPaymentDetails({
      amount: "",
      mode: "",
      date: new Date().toISOString().split("T")[0],
      reference: "",
      remarks: "",
    });
    setAttachmentFile(null);
    setAttachmentPreviewUrl(null);
    onOpenChange(false);
  };

  const isAmountValid = (amountValue = paymentDetails.amount) => {
    const normalizedAmount = String(amountValue ?? "").trim();
    if (!isAmountEntryParsable(normalizedAmount)) return false;

    const amount = parseFloat(normalizedAmount);
    if (isNaN(amount) || amount <= 0) return false;

    if (
      workOrderDetails?.payment_structure === "MILESTONE_BASED" &&
      selectedMilestone
    ) {
      return amount <= selectedMilestone.amount - selectedMilestone.paid_so_far;
    }
    if (
      workOrderDetails?.payment_structure === "MONTHLY_RUNNING_BILL" &&
      selectedMonth
    ) {
      return amount <= selectedMonth.amount - selectedMonth.paid_so_far;
    }
    if (workOrderDetails?.payment_structure === "LUMPSUM_CUSTOM") {
      return amount <= workOrderDetails.financial_summary.balance_due;
    }
    return true;
  };

  const getAmountError = () => {
    if (!paymentDetails.amount) return "";
    if (!isAmountEntryParsable(paymentDetails.amount)) {
      return "Enter a valid amount with up to two decimal places";
    }
    return isAmountValid() ? "" : "Amount exceeds balance due";
  };

  const isContinueDisabled = () => {
    if (step === 1) return !selectedWorkOrder || loading || !workOrderDetails;
    if (step === 2) {
      if (
        workOrderDetails?.payment_structure === "MILESTONE_BASED" &&
        !selectedMilestone
      )
        return true;
      if (
        workOrderDetails?.payment_structure === "MONTHLY_RUNNING_BILL" &&
        !selectedMonth
      )
        return true;
      return !paymentDetails.amount || !paymentDetails.mode || !isAmountValid();
    }
    return false;
  };

  const getNormalizedTaskStatus = (item) =>
    String(
      item?.task_status ||
        item?.taskStatus ||
        item?.status ||
        item?.task_details?.task_status ||
        item?.task_details?.status ||
        "",
    ).toLowerCase();
  const isCompletedTask = (item) =>
    getNormalizedTaskStatus(item).includes("complete");

  const filteredBoqItems = boqItems.filter(
    (item) =>
      !isCompletedTask(item) &&
      ((item.task_name || item.item_name || "")
        .toLowerCase()
        .includes(boqSearchQuery.toLowerCase()) ||
        item.sectionName.toLowerCase().includes(boqSearchQuery.toLowerCase())),
  );

  const getTaskStatusBucket = (item) => {
    const normalizedStatus = getNormalizedTaskStatus(item);

    if (
      normalizedStatus.includes("halt") ||
      normalizedStatus.includes("hold") ||
      normalizedStatus.includes("pause")
    ) {
      return "halted";
    }
    if (
      normalizedStatus.includes("not started") ||
      normalizedStatus.includes("not_started")
    ) {
      return "notStarted";
    }
    if (
      normalizedStatus.includes("ongoing") ||
      normalizedStatus.includes("in progress") ||
      normalizedStatus.includes("in_progress")
    ) {
      return "ongoing";
    }
    return "notStarted";
  };

  const groupedBoqItems = [
    { key: "ongoing", title: "Ongoing" },
    { key: "notStarted", title: "Not Started" },
    { key: "halted", title: "Halted" },
  ]
    .map((group) => ({
      ...group,
      items: filteredBoqItems.filter(
        (item) => getTaskStatusBucket(item) === group.key,
      ),
    }))
    .filter((group) => group.items.length > 0);

  if (!open) return null;

  const dialogContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-3">
      <div className="relative flex w-full max-w-lg max-h-[calc(100vh-1.5rem)] flex-col overflow-hidden rounded-lg border bg-background shadow-lg animate-in fade-in-0 zoom-in-95 duration-200">
        <div className="flex shrink-0 flex-col space-y-1.5 px-5 pb-1 pt-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold leading-none tracking-tight">
              Add Payment
            </h3>
            <button
              onClick={handleClose}
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-sm-accent data-[state=open]:text-muted-foreground"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-1">
          {/* Step Indicator */}
          <div className="flex items-center gap-2 py-1.5 mb-3">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={cn(
                    "h-7 w-7 rounded-full flex items-center justify-center text-xs font-medium",
                    step >= s
                      ? "bg-sm-primary text-white"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {step > s ? <Check className="h-4 w-4" /> : s}
                </div>
                {s < 3 && (
                  <ChevronRight
                    className={cn(
                      "h-4 w-4 mx-1",
                      step > s ? "text-sm-primary" : "text-muted-foreground",
                    )}
                  />
                )}
              </div>
            ))}
            <span className="ml-2 text-sm text-muted-foreground">
              {step === 1 && "Select Work Order"}
              {step === 2 && "Payment Details"}
              {step === 3 && "Review & Confirm"}
            </span>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          {/* Step 1: Select Work Order */}
          {step === 1 && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-sm">Select Work Order</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={selectedWorkOrder?.workorder_id || ""}
                  onChange={(e) => handleSelectWorkOrder(e.target.value)}
                >
                  <option value="" disabled>
                    Choose a work order
                  </option>
                  {workOrders.map((wo) => (
                    <option key={wo.workorder_id} value={wo.workorder_id}>
                      #{wo.id} - {wo.projectName || wo.site}
                    </option>
                  ))}
                </select>
              </div>

              {loading && (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-6 w-6 animate-spin text-sm-primary" />
                </div>
              )}

              {workOrderDetails && !loading && (
                <Card className="border-border/60">
                  <CardContent className="p-4 space-y-3">
                    {/* <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Contract Type</span>
                      <Badge variant="outline" className="text-xs border-border">
                        {workOrderDetails.contract_type}
                      </Badge>
                    </div> */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        Payment Structure
                      </span>
                      <Badge
                        variant="secondary"
                        className="text-xs bg-sm-secondary/10 text-sm-secondary border-0"
                      >
                        {workOrderDetails.payment_structure === "LUMPSUM_CUSTOM"
                          ? "FIXED PRICE (CUSTOM)"
                          : workOrderDetails.payment_structure.replace(
                              /_/g,
                              " ",
                            )}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        Work Order Value
                      </span>
                      <span className="text-sm font-medium">
                        {formatCurrency(
                          workOrderDetails.financial_summary.total_value,
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        Paid Till Now
                      </span>
                      <span className="text-sm font-medium text-green-600">
                        {formatCurrency(
                          workOrderDetails.financial_summary.total_paid,
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-t border-border/60 pt-3">
                      <span className="text-xs font-medium text-muted-foreground">
                        Remaining Due
                      </span>
                      <span className="text-sm font-semibold text-destructive">
                        {formatCurrency(
                          workOrderDetails.financial_summary.balance_due,
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Step 2: Payment Details */}
          {step === 2 && workOrderDetails && (
            <div className="space-y-3 py-1">
              {/* Summary Banner */}
              <Card className="border-sm-secondary/30 bg-sm-secondary/5">
                <CardContent className="flex items-center justify-between px-3 py-2 text-xs">
                  <span>
                    Value:{" "}
                    <strong>
                      {formatCurrency(
                        workOrderDetails.financial_summary.total_value,
                      )}
                    </strong>
                  </span>
                  <span>
                    Paid:{" "}
                    <strong className="text-green-600">
                      {formatCurrency(
                        workOrderDetails.financial_summary.total_paid,
                      )}
                    </strong>
                  </span>
                  <span>
                    Due:{" "}
                    <strong className="text-destructive">
                      {formatCurrency(
                        workOrderDetails.financial_summary.balance_due,
                      )}
                    </strong>
                  </span>
                </CardContent>
              </Card>

              {/* BoQ Task Link Section */}
              {boqItems.length > 0 && (
                <div className="space-y-1.5 border-t border-border/40 pt-2">
                  <Label className="text-xs">Link to Task (Optional)</Label>
                  <div
                    onClick={() => setShowBoQModal(true)}
                    className={cn(
                      "flex items-center justify-between rounded-md border border-dashed px-3 py-2 cursor-pointer transition-all",
                      selectedBoQ
                        ? "border-sm-primary bg-sm-primary/5"
                        : "border-border/60 hover:border-sm-primary/50",
                    )}
                  >
                    {selectedBoQ ? (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium leading-tight text-sm-primary">
                          {selectedBoQ.task_name || selectedBoQ.item_name}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {selectedBoQ.sectionName}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center text-sm text-muted-foreground">
                        <LinkIcon size={14} className="mr-2" />
                        Select a task to link
                      </div>
                    )}
                    <button
                      type="button"
                      className="text-sm-primary text-xs font-semibold"
                    >
                      {selectedBoQ ? "Change" : "Link"}
                    </button>
                  </div>
                  {selectedBoQ && (
                    <button
                      type="button"
                      onClick={() => setSelectedBoQ(null)}
                      className="text-[11px] text-destructive hover:underline font-medium"
                    >
                      Remove link
                    </button>
                  )}
                </div>
              )}

              {/* Milestone Selection (if applicable) */}
              {workOrderDetails.payment_structure === "MILESTONE_BASED" && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Select Milestone *</Label>
                  <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                    {workOrderDetails.milestones.map((milestone) => {
                      const isUnpaid = milestone.amount > milestone.paid_so_far;
                      return (
                        <div
                          key={milestone.id}
                          onClick={() =>
                            isUnpaid && handleSelectMilestone(milestone)
                          }
                          className={cn(
                            "rounded-md border px-3 py-2 cursor-pointer transition-all",
                            !isUnpaid
                              ? "bg-muted/50 border-border/40 cursor-not-allowed opacity-60"
                              : selectedMilestone?.id === milestone.id
                                ? "border-sm-primary bg-sm-primary/5"
                                : "border-border/60 hover:border-sm-primary/50",
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium leading-tight">
                                {milestone.name}
                              </p>
                              <p className="text-xs leading-tight text-muted-foreground">
                                {milestone.description}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium">
                                {formatCurrency(milestone.amount)}
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                Paid: {formatCurrency(milestone.paid_so_far)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Monthly Bill Selection */}
              {workOrderDetails.payment_structure ===
                "MONTHLY_RUNNING_BILL" && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Select Monthly Bill</Label>
                  <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                    {workOrderDetails.monthly_bills.map((bill, idx) => {
                      const isSelectable = bill.status !== "PAID";
                      return (
                        <div
                          key={idx}
                          onClick={() =>
                            isSelectable && handleSelectMonth(bill, idx)
                          }
                          className={cn(
                            "rounded-md border px-3 py-2 cursor-pointer transition-all",
                            !isSelectable
                              ? "bg-muted/50 border-border/40 cursor-not-allowed opacity-60"
                              : selectedMonth?.index === idx + 1
                                ? "border-sm-primary bg-sm-primary/5"
                                : "border-border/60 hover:border-sm-primary/50",
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium leading-tight">
                                {bill.month}
                              </p>
                              <p className="text-xs leading-tight text-muted-foreground">
                                Progress: {bill.progress}%
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium">
                                {formatCurrency(bill.amount)}
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                Paid: {formatCurrency(bill.paid_so_far)}
                              </p>
                              <Badge
                                className={cn(
                                  "text-[10px]",
                                  bill.status === "PAID"
                                    ? "bg-green-500/10 text-green-600"
                                    : "bg-orange-500/10 text-orange-600",
                                )}
                              >
                                {bill.status}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Common Payment Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Payment Amount *</Label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="number"
                      placeholder="Enter amount"
                      value={paymentDetails.amount}
                      onChange={handleAmountChange}
                      step="0.01"
                      min="0.00"
                      // readOnly={workOrderDetails.payment_structure === "MONTHLY_RUNNING_BILL"}
                      className={cn(
                        "flex h-9 w-full rounded-md border border-input bg-transparent pl-9 pr-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ",
                        workOrderDetails.payment_structure ===
                          "MONTHLY_RUNNING_BILL" && "bg-muted",
                      )}
                    />
                  </div>
                  {step === 2 && getAmountError() && (
                    <p className="text-[10px] text-destructive">
                      {getAmountError()}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Payment Mode *</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    value={paymentDetails.mode}
                    onChange={(e) =>
                      setPaymentDetails({
                        ...paymentDetails,
                        mode: e.target.value,
                      })
                    }
                  >
                    <option value="" disabled>
                      Select mode
                    </option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="CASH">Cash</option>
                    <option value="ONLINE">Online/UPI</option>
                    <option value="CHEQUE">Cheque</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Payment Date</Label>
                  <div className="relative">
                    <Input
                      type="date"
                      className="block h-9"
                      value={paymentDetails.date}
                      onChange={(e) =>
                        setPaymentDetails({
                          ...paymentDetails,
                          date: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Reference/UTR No.</Label>
                  <Input
                    placeholder="TXN_123456"
                    value={paymentDetails.reference}
                    onChange={(e) =>
                      setPaymentDetails({
                        ...paymentDetails,
                        reference: e.target.value,
                      })
                    }
                    className="h-9"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Remarks</Label>
                <Textarea
                  placeholder="Add any notes..."
                  value={paymentDetails.remarks}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      remarks: e.target.value,
                    })
                  }
                  className="h-12 resize-none"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Attachment (PDF or File)</Label>
                {!attachmentFile && (
                  <label className="flex h-12 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 bg-muted/20 px-3 py-2 text-xs font-semibold text-sm-primary transition-colors hover:border-sm-primary/80 hover:bg-sm-primary/5">
                    <input
                      type="file"
                      accept="application/pdf,image/*"
                      onChange={handleAttachmentChange}
                      className="hidden"
                    />
                    <Upload className="h-4 w-4" />
                    Upload attachment
                    <span className="text-[10px] font-normal text-muted-foreground">
                      PDF or image
                    </span>
                  </label>
                )}
                {attachmentFile && (
                  <div className="relative overflow-hidden rounded-md border border-border bg-muted/20">
                    {attachmentPreviewUrl ? (
                      <div className="h-28 bg-gray-50">
                        <img
                          src={attachmentPreviewUrl}
                          alt="Attachment preview"
                          className="h-full w-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 pr-11">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-sm-primary/10 text-sm-primary">
                          <FileText className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">
                            {attachmentFile.name}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {formatFileSize(attachmentFile.size) ||
                              attachmentFile.type ||
                              "Attachment"}
                          </p>
                        </div>
                      </div>
                    )}
                    {attachmentPreviewUrl && (
                      <div className="border-t border-border bg-white px-3 py-2 pr-11">
                        <p className="truncate text-xs font-medium">
                          {attachmentFile.name}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {formatFileSize(attachmentFile.size)}
                        </p>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={clearAttachment}
                      className="absolute right-2 top-2 rounded-full bg-red-500 p-1 text-white shadow hover:bg-red-600"
                      aria-label="Clear attachment"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Review & Confirm */}
          {step === 3 && workOrderDetails && (
            <div className="space-y-4 py-2">
              <Card className="border-border/60">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-3 pb-3 border-b border-border/60">
                    <div className="h-10 w-10 rounded-lg bg-sm-secondary/10 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-sm-secondary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Payment Summary</p>
                      <p className="text-xs text-muted-foreground">
                        Review before confirming
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Work Order</span>
                      <span className="font-medium">
                        #{workOrderDetails.id}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Contractor</span>
                      <span className="font-medium">
                        {workOrderDetails.contractor_name}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Payment Type
                      </span>
                      <span className="font-medium">
                        {workOrderDetails.payment_structure.replace(/_/g, " ")}
                      </span>
                    </div>
                    {selectedMilestone && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Milestone</span>
                        <span className="font-medium">
                          {selectedMilestone.name}
                        </span>
                      </div>
                    )}
                    {selectedMonth && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Month</span>
                        <span className="font-medium">
                          {selectedMonth.month}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Payment Mode
                      </span>
                      <span className="font-medium">
                        {paymentDetails.mode.replace(/_/g, " ")}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Payment Date
                      </span>
                      <span className="font-medium">
                        {new Date(paymentDetails.date).toLocaleDateString(
                          "en-IN",
                          {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          },
                        )}
                      </span>
                    </div>
                    {paymentDetails.reference && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Reference</span>
                        <span className="font-medium">
                          {paymentDetails.reference}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t border-border/60">
                    <span className="text-sm font-medium">Amount to Pay</span>
                    <div className="text-right">
                      <span className="text-xl font-bold text-green-600">
                        {formatCurrency(parseFloat(paymentDetails.amount))}
                      </span>
                      {selectedMilestone && (
                        <p className="text-[10px] text-muted-foreground">
                          Remaining for milestone:{" "}
                          {formatCurrency(
                            selectedMilestone.amount -
                              selectedMilestone.paid_so_far -
                              parseFloat(paymentDetails.amount),
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between border-t bg-muted/10 p-3">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={step === 1 || submitting}
          >
            Back
          </Button>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            {step < 3 ? (
              <Button onClick={handleNext} disabled={isContinueDisabled()}>
                Continue
              </Button>
            ) : (
              <Button
                onClick={handleConfirm}
                disabled={submitting}
                className="hover:bg-sm-primary/90"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Confirm & Record Payment
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Task Selection Modal */}
      {showBoQModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-background rounded-lg w-full max-w-md max-h-[70vh] flex flex-col shadow-2xl overflow-hidden border">
            <div className="px-6 py-4 border-b flex justify-between items-center bg-muted/30">
              <h3 className="text-sm font-semibold">Link to BoQ Task</h3>
              <button
                type="button"
                onClick={() => setShowBoQModal(false)}
                className="p-1 rounded-md hover:bg-muted text-muted-foreground transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-4 border-b bg-background">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  size={14}
                />
                <input
                  type="text"
                  placeholder="Search BoQ items or sections..."
                  value={boqSearchQuery}
                  onChange={(e) => setBoqSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-muted/50 border-none rounded-md focus:ring-1 focus:ring-sm-primary text-sm outline-none transition-all"
                  autoFocus
                />
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-2 bg-background">
              {groupedBoqItems.map((group) => (
                <div key={group.key} className="mb-2">
                  <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {group.title}
                  </p>
                  {group.items.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => {
                        setSelectedBoQ(item);
                        setShowBoQModal(false);
                      }}
                      className={cn(
                        "p-3 rounded-md cursor-pointer mb-1 transition-all border",
                        selectedBoQ?.id === item.id
                          ? "bg-sm-primary/5 border-sm-primary/20"
                          : "hover:bg-muted border-transparent",
                      )}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-sm font-medium">
                            {item.task_name || item.item_name}
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            {item.sectionName}
                          </div>
                        </div>
                        {selectedBoQ?.id === item.id && (
                          <Check className="h-4 w-4 text-sm-primary" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {filteredBoqItems.length === 0 && (
                <div className="py-8 text-center">
                  <p className="text-muted-foreground text-xs font-medium">
                    No matching tasks found
                  </p>
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-muted/20">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowBoQModal(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (typeof document === "undefined") {
    return dialogContent;
  }

  return createPortal(dialogContent, document.body);
}
